import { useState } from 'react';
import { z } from 'zod';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';

const RecordSchema = z.record(z.string(), z.unknown());
const ScalarSortValueSchema = z.union([z.string(), z.number(), z.boolean()]);
const StringSortValueSchema = z.string();
const BooleanSortValueSchema = z.boolean();
type ScalarSortValue = z.infer<typeof ScalarSortValueSchema>;

export interface Column<T> {
    key: string;
    header: string;
    sortable?: boolean;
    render?: (item: T) => React.ReactNode;
    sortValue?: (item: T) => ScalarSortValue | null | undefined;
}

interface DataTableProps<T> {
    data: T[];
    columns: Column<T>[];
    keyExtractor: (item: T) => string;
    onRowClick?: (item: T) => void;
    pageSize?: number;
    paginationMode?: 'client' | 'none';
}

function getColumnSortValue<T>(
    item: T,
    column: Column<T>
): ScalarSortValue | null | undefined {
    if (column.sortValue !== undefined) {
        return column.sortValue(item);
    }

    const parseResult = RecordSchema.safeParse(item);
    if (!parseResult.success) {
        return undefined;
    }

    const value = parseResult.data[column.key];
    const valueParseResult = ScalarSortValueSchema.safeParse(value);
    if (!valueParseResult.success) {
        return undefined;
    }

    return valueParseResult.data;
}

function compareScalarSortValues(
    left: ScalarSortValue | null | undefined,
    right: ScalarSortValue | null | undefined,
    direction: 'asc' | 'desc'
): number {
    const leftMissing = left === null || left === undefined;
    const rightMissing = right === null || right === undefined;

    if (leftMissing && rightMissing) {
        return 0;
    }
    if (leftMissing) {
        return direction === 'asc' ? 1 : -1;
    }
    if (rightMissing) {
        return direction === 'asc' ? -1 : 1;
    }

    const leftStringParseResult = StringSortValueSchema.safeParse(left);
    const rightStringParseResult = StringSortValueSchema.safeParse(right);
    if (leftStringParseResult.success && rightStringParseResult.success) {
        const comparison = leftStringParseResult.data.localeCompare(
            rightStringParseResult.data,
            undefined,
            {
            sensitivity: 'base',
            numeric: true,
            }
        );
        return direction === 'asc' ? comparison : -comparison;
    }

    const leftBooleanParseResult = BooleanSortValueSchema.safeParse(left);
    const rightBooleanParseResult = BooleanSortValueSchema.safeParse(right);
    const normalizedLeft =
        leftBooleanParseResult.success ? Number(leftBooleanParseResult.data) : left;
    const normalizedRight =
        rightBooleanParseResult.success ? Number(rightBooleanParseResult.data) : right;

    if (normalizedLeft === normalizedRight) {
        return 0;
    }

    const comparison = normalizedLeft < normalizedRight ? -1 : 1;
    return direction === 'asc' ? comparison : -comparison;
}

export function DataTable<T>({
    data,
    columns,
    keyExtractor,
    onRowClick,
    pageSize = 10,
    paginationMode = 'client',
}: DataTableProps<T>) {
    const [sortKey, setSortKey] = useState<string | null>(null);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const usesClientPagination: boolean = paginationMode === 'client';

    const handleSort = (key: string) => {
        if (sortKey === key) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortKey(key);
            setSortDirection('asc');
        }
    };

    const sortedData = [...data].sort((leftItem, rightItem) => {
        if (sortKey === null) return 0;

        const sortColumn = columns.find((column) => column.key === sortKey);
        if (sortColumn === undefined) {
            return 0;
        }

        return compareScalarSortValues(
            getColumnSortValue(leftItem, sortColumn),
            getColumnSortValue(rightItem, sortColumn),
            sortDirection
        );
    });

    const totalPages = usesClientPagination ? Math.ceil(sortedData.length / pageSize) : 1;
    const startIndex = usesClientPagination ? (currentPage - 1) * pageSize : 0;
    const visibleData = usesClientPagination
        ? sortedData.slice(startIndex, startIndex + pageSize)
        : sortedData;

    return (
        <div className="space-y-4">
            <div className="rounded-md border">
                <Table>
                    <TableHeader>
                        <TableRow>
                            {columns.map((column) => (
                                <TableHead key={column.key}>
                                    {column.sortable ? (
                                        <Button
                                            variant="ghost"
                                            onClick={() => handleSort(column.key)}
                                            className="h-8 px-2 -ml-2"
                                        >
                                            {column.header}
                                            {sortKey === column.key && (
                                                <span className="ml-1">
                                                    {sortDirection === 'asc' ? '↑' : '↓'}
                                                </span>
                                            )}
                                        </Button>
                                    ) : (
                                        column.header
                                    )}
                                </TableHead>
                            ))}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {visibleData.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={columns.length}
                                    className="h-24 text-center text-muted-foreground"
                                >
                                    No results found.
                                </TableCell>
                            </TableRow>
                        ) : (
                            visibleData.map((item) => (
                                <TableRow
                                    key={keyExtractor(item)}
                                    onClick={() => onRowClick?.(item)}
                                    className={onRowClick ? 'cursor-pointer hover:bg-muted/50' : ''}
                                >
                                    {columns.map((column) => (
                                        <TableCell key={column.key}>
                                            {column.render
                                                ? column.render(item)
                                                : (() => {
                                                      const parseResult =
                                                          RecordSchema.safeParse(item);
                                                      if (!parseResult.success) {
                                                          return '';
                                                      }
                                                      const value = parseResult.data[column.key];
                                                      return value !== undefined && value !== null
                                                          ? String(value)
                                                          : '';
                                                  })()}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            {usesClientPagination && totalPages > 1 && (
                <div className="flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">
                        Showing {startIndex + 1} to{' '}
                        {Math.min(startIndex + pageSize, sortedData.length)} of {sortedData.length}{' '}
                        results
                    </div>
                    <div className="flex items-center space-x-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(currentPage - 1)}
                            disabled={currentPage === 1}
                        >
                            Previous
                        </Button>
                        <div className="text-sm">
                            Page {currentPage.toString()} of {totalPages}
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(currentPage + 1)}
                            disabled={currentPage === totalPages}
                        >
                            Next
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
